import React, { useState, useEffect, useCallback, useRef } from 'react';
import PinLogin from './components/PinLogin';
import Dashboard from './components/Dashboard';
import Pointage from './components/Pointage';
import PointageReleve from './components/PointageReleve';
import FicheAdherent from './components/FicheAdherent';
import FicheEtablissement from './components/FicheEtablissement';
import RechercheAvancee from './components/RechercheAvancee';
import Statistiques from './components/Statistiques';
import NouvelleAdhesion from './components/NouvelleAdhesion';
import DashboardDS from './components/DashboardDS';
import ValidationAdhesions from './components/ValidationAdhesions';
import ConseilSyndical, { MEMBRES_INIT } from './components/ConseilSyndical';
import SuiviDepenses from './components/SuiviDepenses';
import ComptabiliteFinancier from './components/ComptabiliteFinancier';
import FoLogo from './components/FoLogo';
import { uid, journalEntry } from './utils/helpers';
import { SEED_ADHERENTS } from './data/seedAdherents';
import { SEED_VIREMENT } from './data/seedVirement';
import { hashPassword } from './utils/cryptoUtils';
import { applySeedCotisations2026 } from './data/seedCotisations2026';
import { applyUpdates2026 } from './data/seedUpdates2026';
import { loadRemoteState, saveRemoteState, subscribeToState, loadRemoteStateFull, uploadToStorage, deleteFromStorage, getStoragePublicUrl, saveEntitiesToTables, loadEntitiesFromTables, subscribeToEntities } from './supabaseClient';

const STORAGE_KEY  = 'sdassp_fo_data_v3';
const BACKUP_KEY   = 'sdassp_fo_autobackup'; // sauvegarde quotidienne automatique
const SESSION_KEY  = 'sdassp_fo_session';
const SESSION_TTL = 15 * 60 * 1000; // 15 minutes
const getSession = () => { try { const s = JSON.parse(sessionStorage.getItem(SESSION_KEY)) || {}; if (s.expiresAt && Date.now() > s.expiresAt) { sessionStorage.removeItem(SESSION_KEY); return {}; } return s; } catch { return {}; } };

const ALL_PAGES = [
  { id: 'dashboard',      label: 'Tableau de bord',   icon: '📊', roles: ['tresorier'] },
  { id: 'accueil_ds',    label: 'Mon espace',         icon: '👤', roles: ['delegue'] },
  { id: 'pointage',      label: 'Pointage',           icon: '📅', roles: ['tresorier'] },
  { id: 'pointage_releve', label: 'Pointage Relevé',    icon: '🏦', roles: ['tresorier'] },
  { id: 'adherents',     label: 'Adhérents',         icon: '👥', roles: ['tresorier', 'bureau_simple', 'delegue'] },
  { id: 'recherche',     label: 'Recherche',          icon: '🔍', roles: ['tresorier', 'delegue', 'bureau_simple'] },
  { id: 'etablissements',label: 'Structures',         icon: '🏢', roles: ['tresorier'] },
  { id: 'statistiques',  label: 'Statistiques',       icon: '📈', roles: ['tresorier', 'bureau_simple', 'delegue'] },
  { id: 'adhesion',      label: 'Nouvelle adhésion', icon: '➕', roles: ['tresorier', 'bureau_simple', 'delegue'] },
  { id: 'validation',    label: 'Validations',        icon: '✅', roles: ['tresorier'], badge: 'demandes' },
  { id: 'conseil',       label: 'Conseil Syndical',   icon: '⚖️', roles: ['tresorier'], badge: 'convocations' },
  { id: 'depenses',      label: 'Dépenses',          icon: '💰', roles: ['tresorier', 'bureau_simple'] },
  { id: 'comptabilite',  label: 'Comptabilité',       icon: '📒', roles: ['tresorier'] },
];

function mergeVirement(state) {
  if (state._virementImported) return state;
  const normalize = s => (s || '').trim().toUpperCase().replace(/\s+/g, ' ');
  const existing = new Set((state.adherents || []).map(a => normalize(a.nom) + '|' + normalize(a.prenom)));
  let etabs = [...(state.etablissements || [])];
  const getOrCreateEtab = (nom) => {
    if (!nom) return '';
    const found = etabs.find(e => normalize(e.nom) === normalize(nom));
    if (found) return found.id;
    const newEtab = { id: uid(), nom: nom.trim() };
    etabs.push(newEtab);
    return newEtab.id;
  };
  const adherents = [...(state.adherents || [])];
  const year = new Date().getFullYear();
  SEED_VIREMENT.forEach(p => {
    const key = normalize(p.nom) + '|' + normalize(p.prenom);
    if (existing.has(key)) return;
    existing.add(key);
    const seq = String(adherents.length + 1).padStart(3, '0');
    adherents.push({
      id: uid(), prenom: p.prenom, nom: p.nom,
      numero: `FO-${year}-${seq}`, statut: 'Actif',
      etablissementId: getOrCreateEtab(p.etab),
      metier: '', contrat: 'CDI', dateEntree: '',
      convention: 'CCN 51', coefficient: '', dateAdhesion: '',
      modePaiement: 'Virement', jourPrelevement: '',
      periodicite: 'Mensuelle', montant: p.montant || 0,
      rib: '', telephone: '', email: '', adresse: '',
      notes: '', delegueNom: '', createdBy: 'Import Virement 2023',
    });
  });
  return { ...state, adherents, etablissements: etabs, _virementImported: true };
}

function getInitialData() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return parsed;
    }
  } catch {}
  return null;
}

function defaultState() {
  return {
    adherents: SEED_ADHERENTS,
    etablissements: [],
    cotisations: {},
    solde: 0,
    selectedYear: new Date().getFullYear(),
    journal: [],
    connexions: [],
    pins: { delegue: null, tresorier: null },
    users: [{
      id: 'usr-admin-1',
      username: 'admin',
      password: 'Admin1234',
      role: 'tresorier',
      roleTitle: 'Administrateur',
      isAdmin: true,
      mustChangePassword: true,
      createdAt: new Date().toISOString(),
    }],
    demandes: [],
    conseilMembers: MEMBRES_INIT,
    convocations: [],
    depenses: [],
    budgetDepenses: {},
    _deletedIds: [],
    compteResultat: {
      historique: {
        cotisations:      { 2021: 19094.90, 2022: 18058.25, 2023: 35846.00, 2024: 37756.10 },
        subvention_ud:    { 2021: 340.00,   2022: 0,        2023: 1000.00,  2024: 1200.00  },
        subvention_gd:    { 2021: 1211.75,  2022: 0,        2023: 0,        2024: 1000.00  },
        subvention_unsfo: { 2021: 3478.35,  2022: 0,        2023: 4756.78,  2024: 1188.56  },
        participation:    { 2021: 0,        2022: 0,        2023: 1000.00,  2024: 0        },
        cartes_timbres:   { 2021: 12519.00, 2022: 15334.11, 2023: 22854.70, 2024: 23909.60 },
        service_bancaire: { 2021: 556.37,   2022: 444.62,   2023: 676.92,   2024: 696.15   },
        frais_prelevement:{ 2021: 42.70,    2022: 0,        2023: 1558.30,  2024: 2888.57  },
        frais_postaux:    { 2021: 0,        2022: 61.00,    2023: 0,        2024: 0        },
        fournitures:      { 2021: 164.37,   2022: 1556.16,  2023: 2950.91,  2024: 39.99    },
        manifestation:    { 2021: 1401.70,  2022: 0,        2023: 117.70,   2024: 174.00   },
        deplacement:      { 2021: 2646.90,  2022: 0,        2023: 8852.45,  2024: 3244.71  },
        restauration:     { 2021: 498.45,   2022: 873.68,   2023: 2456.95,  2024: 4231.85  },
        cotisation_unsfo: { 2021: 629.28,   2022: 162.64,   2023: 712.24,   2024: 547.20   },
        subvention_versee:{ 2021: 200.00,   2022: 0,        2023: 200.00,   2024: 0        },
      },
    },
  };
}

function migrate(saved) {
  if (!saved) return null;
  if (saved.pin !== undefined && !saved.pins) {
    saved.pins = { delegue: null, tresorier: saved.pin || null };
    delete saved.pin;
  }
  if (!saved.pins) saved.pins = { delegue: null, tresorier: null };
  if (!saved.demandes) saved.demandes = [];
  if (!saved.conseilMembers) saved.conseilMembers = MEMBRES_INIT;
  if (!saved.convocations) saved.convocations = [];
  if (!saved.depenses) saved.depenses = [];
  if (!saved.budgetDepenses) saved.budgetDepenses = {};
  if (!saved._deletedIds) saved._deletedIds = [];
  if (!saved.users) {
    const adminPwd = saved.pins?.tresorier || 'Admin1234';
    saved.users = [{
      id: 'usr-admin-1',
      username: 'admin',
      password: adminPwd,
      role: 'tresorier',
      roleTitle: 'Administrateur',
      isAdmin: true,
      mustChangePassword: !saved.pins?.tresorier,
      createdAt: new Date().toISOString(),
    }];
    if (saved.pins?.delegue) {
      saved.users.push({
        id: 'usr-delegue-1',
        username: 'delegue',
        password: saved.pins.delegue,
        role: 'delegue',
        roleTitle: 'Délégué(e) syndical(e)',
        isAdmin: false,
        mustChangePassword: false,
        createdAt: new Date().toISOString(),
      });
    }
  }
  return applyUpdates2026(applySeedCotisations2026(mergeVirement(saved)));
}

// Migration schema uniquement — JAMAIS de seed sur les données distantes
function migrateRemote(saved) {
  if (!saved) return null;
  if (saved.pin !== undefined && !saved.pins) {
    saved.pins = { delegue: null, tresorier: saved.pin || null };
    delete saved.pin;
  }
  if (!saved.pins) saved.pins = { delegue: null, tresorier: null };
  if (!saved.demandes) saved.demandes = [];
  if (!saved.conseilMembers) saved.conseilMembers = MEMBRES_INIT;
  if (!saved.convocations) saved.convocations = [];
  if (!saved.depenses) saved.depenses = [];
  if (!saved.budgetDepenses) saved.budgetDepenses = {};
  if (!saved._deletedIds) saved._deletedIds = [];
  if (!saved.users) {
    const adminPwd = saved.pins?.tresorier || 'Admin1234';
    saved.users = [{ id: 'usr-admin-1', username: 'admin', password: adminPwd,
      role: 'tresorier', roleTitle: 'Administrateur', isAdmin: true,
      mustChangePassword: !saved.pins?.tresorier, createdAt: new Date().toISOString() }];
  }
  return saved; // ← pas de mergeVirement, applySeedCotisations2026, applyUpdates2026
}

// Utilitaire pur : dédoublonne les opérations échouées par clé (type+action+name).
// Raison : plusieurs snapshots échoués peuvent contenir la même opération logique
// (ex : 3 sauvegardes ratées pour la même création d'adhérent = 1 seule entrée visible).
// Max 20 entrées, les plus récentes en dernier.
const MAX_FAILED_OPS = 20;
function dedupeFailedOps(ops) {
  const seen = new Set();
  const result = [];
  for (const op of ops) {
    const key = `${op.type}|${op.action}|${op.name}`;
    if (!seen.has(key)) { seen.add(key); result.push(op); }
    if (result.length >= MAX_FAILED_OPS) break;
  }
  return result;
}

export default function App() {
  const [data, setData] = useState(() => migrate(getInitialData()) || applyUpdates2026(applySeedCotisations2026(defaultState())));
  const [page, setPage] = useState('dashboard');
  const [selectedAdherentId, setSelectedAdherentId] = useState(null);
  const [selectedEtabId, setSelectedEtabId] = useState(null);
  const [currentRole, setCurrentRole] = useState(() => getSession().role || null);
  const [currentRoleTitle, setCurrentRoleTitle] = useState(() => getSession().roleTitle || '');
  const [currentUsername, setCurrentUsername] = useState(() => getSession().username || '');
  const [isAdmin, setIsAdmin] = useState(() => getSession().isAdmin || false);
  const [showSettings, setShowSettings] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [pwaPrompt, setPwaPrompt] = useState(null);
  const [pwaInstalled, setPwaInstalled] = useState(false);
  const [syncStatus, setSyncStatus] = useState('ok'); // 'ok' | 'syncing' | 'remote'
  const [remoteLoading, setRemoteLoading] = useState(true); // bloque le login jusqu'au premier chargement Supabase
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  const applyingRemote = useRef(true);  // true = bloque le premier save avant le chargement Supabase
  const lastSavedAt = useRef(0); // timestamp of last confirmed Supabase save
  const minKnownUsers = useRef((data.users || []).length || 1); // guard: never save fewer users than this
  const logoutTimer = useRef(null);
  const warningTimer = useRef(null);
  const immediateSync = useRef(false); // bypass debounce pour ops critiques
  const dataRef = useRef(data); // toujours frais pour beforeunload
  const savedStateRef = useRef(null); // dernier état confirmé en base (pour delta saves)
  // ── Suivi des opérations critiques en attente de confirmation Supabase ──
  const pendingOps = useRef(new Map()); // opId → { type, action, name, ts }
  const [pendingSyncCount, setPendingSyncCount] = useState(0);
  const [failedSyncOps,   setFailedSyncOps]    = useState([]);
  const isRetrying = useRef(false); // guard : empêche l'empilement de retries

  const INACTIVITY_MS = 60 * 60 * 1000; // 60 minutes
  const WARNING_MS = 55 * 60 * 1000;    // avertissement à 55 min

  const doLogoutFn = () => {
    setCurrentRole(null); setCurrentRoleTitle(''); setIsAdmin(false); setCurrentUsername('');
    setPage('dashboard'); setSidebarOpen(false); setShowTimeoutWarning(false);
    try { sessionStorage.removeItem(SESSION_KEY); } catch {}
  };
  const doLogoutRef = useRef(doLogoutFn);

  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setPwaPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', () => { setPwaInstalled(true); setPwaPrompt(null); });
    return () => { window.removeEventListener('beforeinstallprompt', handler); };
  }, []);
  doLogoutRef.current = doLogoutFn;
  const doLogout = () => doLogoutRef.current();

  const currentRoleRef = useRef(currentRole);
  currentRoleRef.current = currentRole;

  const resetTimersFn = () => {
    if (!currentRoleRef.current) return;
    setShowTimeoutWarning(false);
    clearTimeout(logoutTimer.current);
    clearTimeout(warningTimer.current);
    warningTimer.current = setTimeout(() => setShowTimeoutWarning(true), WARNING_MS);
    logoutTimer.current = setTimeout(() => doLogoutRef.current(), INACTIVITY_MS);
  };
  const resetTimersRef = useRef(resetTimersFn);
  resetTimersRef.current = resetTimersFn;
  const resetTimers = () => resetTimersRef.current();

  // Enregistre une opération critique comme "en attente de confirmation Supabase"
  // Appelé AVANT setData dans chaque fonction critique (saveAdherent, deleteUser, etc.)
  const trackOp = useCallback((type, action, name) => {
    const opId = uid();
    pendingOps.current.set(opId, { type, action, name, ts: new Date().toLocaleTimeString('fr-FR') });
    setPendingSyncCount(pendingOps.current.size);
  }, []); // eslint-disable-line

  // Retry manuel : relance saveRemoteState avec l'état le plus récent.
  // Guard isRetrying : empêche l'empilement si un retry est déjà en cours.
  // Pas de retry automatique : l'utilisateur clique explicitement.
  const retrySync = useCallback(() => {
    if (isRetrying.current) return;
    isRetrying.current = true;
    setSyncStatus('syncing');
    saveRemoteState(dataRef.current).then(ok => {
      isRetrying.current = false;
      if (ok) {
        lastSavedAt.current = Date.now();
        pendingOps.current.clear();
        setPendingSyncCount(0);
        setFailedSyncOps([]);
        setSyncStatus('ok');
      } else {
        setSyncStatus('error');
        // failedSyncOps conservé : l'utilisateur voit toujours les ops non sauvegardées
      }
    });
  }, []); // eslint-disable-line

  // Smart merge: remote wins for known items, but always preserve local items absent from remote
  const smartMerge = (migrated, prev, savedAt) => {
    const ts = savedAt ?? lastSavedAt.current;
    // Union des IDs supprimés localement et distants → jamais réinjectés
    const deletedIds = new Set([
      ...(prev._deletedIds || []),
      ...(migrated._deletedIds || []),
    ]);

    // Restaurer base64 depuis local si le remote n'a pas encore d'URL Storage
    const restoreDocs = (remoteDocs = [], localDocs = []) => {
      const localMap = new Map((localDocs || []).map(d => [d.id, d]));
      const remoteDocIds = new Set(remoteDocs.map(d => d.id));
      const restored = remoteDocs.map(d => {
        if (d.url) return d;
        const local = localMap.get(d.id);
        return (local?.data && local.data !== '[base64]') ? { ...d, data: local.data } : d;
      });
      (localDocs || []).forEach(d => { if (!remoteDocIds.has(d.id)) restored.push(d); });
      return restored;
    };

    const mergeById = (remoteArr = [], localArr = []) => {
      const remoteIds = new Set(remoteArr.map(x => x.id));
      return [
        ...remoteArr.filter(x => !deletedIds.has(x.id)),
        ...localArr.filter(x => {
          if (remoteIds.has(x.id)) return false;
          if (deletedIds.has(x.id)) return false;
          return true; // toujours conserver les items locaux absents du remote (non supprimés)
        }),
      ];
    };

    const localAdhMap   = new Map((prev.adherents || []).map(a => [a.id, a]));
    const localDemMap   = new Map((prev.demandes  || []).map(d => [d.id, d]));
    const localUsersMap = new Map((prev.users     || []).map(u => [u.id, u]));

    // Adherents: comparaison updatedAt — la version la plus récente (remote ou local) gagne
    const adherents = mergeById(migrated.adherents || [], prev.adherents || []).map(a => {
      const local = localAdhMap.get(a.id);
      if (!local) return a;
      const remoteTs = new Date(a.updatedAt    || a.dateAdhesion || 0).getTime();
      const localTs  = new Date(local.updatedAt || local.dateAdhesion || 0).getTime();
      if (localTs > remoteTs) {
        return { ...a, ...local, documents: restoreDocs(a.documents, local.documents) };
      }
      return { ...a, documents: restoreDocs(a.documents, local.documents) };
    });

    // Demandes: merge + restaurer données locales si pas encore uploadées
    const demandes = mergeById(migrated.demandes || [], prev.demandes || []).map(d => {
      const local = localDemMap.get(d.id);
      if (!local) return d;
      const doc = d.document && d.document !== '[base64]' ? d.document
        : (local.document && local.document !== '[base64]' ? local.document : d.document);
      const sepa = d.sepaDocument?.url ? d.sepaDocument
        : (d.sepaDocument && !d.sepaDocument.data && local.sepaDocument?.data
            ? { ...d.sepaDocument, data: local.sepaDocument.data } : d.sepaDocument);
      const comp = d.complementDocument?.url ? d.complementDocument
        : (d.complementDocument && !d.complementDocument.data && local.complementDocument?.data
            ? { ...d.complementDocument, data: local.complementDocument.data } : d.complementDocument);
      return { ...d, document: doc, sepaDocument: sepa, complementDocument: comp };
    });

    const merged = {
      ...migrated,
      adherents,
      demandes,
      etablissements: mergeById(
        (migrated.etablissements || []).filter(e => !deletedIds.has(e.id)),
        (prev.etablissements || []).filter(e => !deletedIds.has(e.id))
      ),
      depenses:       mergeById(migrated.depenses  || [], prev.depenses  || []),
      cotisations:    (() => {
        // Fusion profonde : année → adhId → mois (local wins par mois)
        const rem = migrated.cotisations || {};
        const loc = prev.cotisations || {};
        const result = {};
        const years = new Set([...Object.keys(rem), ...Object.keys(loc)]);
        for (const y of years) {
          const remY = rem[y] || {};
          const locY = loc[y] || {};
          result[y] = {};
          const adhs = new Set([...Object.keys(remY), ...Object.keys(locY)]);
          for (const id of adhs) {
            result[y][id] = { ...(remY[id] || {}), ...(locY[id] || {}) };
          }
        }
        return result;
      })(),
      convocations:   mergeById(migrated.convocations  || [], prev.convocations  || []),
      conseilMembers: mergeById(migrated.conseilMembers || [], prev.conseilMembers || []),
      journal:        mergeById(migrated.journal   || [], prev.journal   || []).slice(0, 200),
      connexions:     mergeById(migrated.connexions || [], prev.connexions || []).slice(0, 100),
      // Utilisateurs : Supabase est la source de vérité.
      // Sur chargement initial (ts=0), on n'injecte PAS les anciens utilisateurs locaux
      // pour éviter que des comptes supprimés ou obsolètes réapparaissent.
      users: (() => {
        const remoteUsers = (migrated.users || []).filter(u => !deletedIds.has(u.id));
        const remoteIds   = new Set(remoteUsers.map(u => u.id));
        // Ajouter uniquement les utilisateurs locaux créés APRÈS le dernier save (nouveaux non encore synchros)
        const localNew = (prev.users || []).filter(u => {
          if (remoteIds.has(u.id))    return false;
          if (deletedIds.has(u.id))   return false;
          if (ts === 0)               return false; // chargement initial → on ne réinjecte pas les anciens
          const created = new Date(u.createdAt || 0).getTime();
          return created > ts;
        });
        return [...remoteUsers, ...localNew].map(u => {
          const loc = localUsersMap.get(u.id);
          if (!loc) return u;
          return {
            ...u, ...loc,
            password:           u.password,
            mustChangePassword: u.mustChangePassword,
            role:               u.role,
            isAdmin:            u.isAdmin,
            roleTitle:          u.roleTitle,
            etablissementsIds:  u.etablissementsIds,
          };
        });
      })(),
      _deletedIds:    [...deletedIds],
      budgetDepenses: { ...(migrated.budgetDepenses || {}), ...(prev.budgetDepenses || {}) },
      // Fix sync comptabilité : fusion profonde année/mois (le mois avec dateImport le plus récent gagne)
      comptabilite: (() => {
        const rem = migrated.comptabilite || {};
        const loc = prev.comptabilite || {};
        const result = {};
        const years = new Set([...Object.keys(rem), ...Object.keys(loc)]);
        for (const y of years) {
          const remY = rem[y] || {};
          const locY = loc[y] || {};
          result[y] = {};
          const months = new Set([...Object.keys(remY), ...Object.keys(locY)]);
          for (const m of months) {
            const remM = remY[m]; const locM = locY[m];
            if (!remM) { result[y][m] = locM; continue; }
            if (!locM) { result[y][m] = remM; continue; }
            const remDate = new Date(remM.dateImport || 0).getTime();
            const locDate = new Date(locM.dateImport || 0).getTime();
            result[y][m] = locDate > remDate ? locM : remM;
          }
        }
        return result;
      })(),
      compteResultat: (() => {
        const rem = (migrated.compteResultat?.historique) || {};
        const loc = (prev.compteResultat?.historique) || {};
        const hist = {};
        const keys = new Set([...Object.keys(rem), ...Object.keys(loc)]);
        for (const k of keys) {
          const remEntry = rem[k] || {};
          const locEntry = loc[k] || {};
          const remTs = remEntry._updatedAt || '1970-01-01';
          const locTs = locEntry._updatedAt || '1970-01-01';
          // Le plus récent gagne ; sans timestamp, remote (Supabase) gagne
          hist[k] = remTs >= locTs
            ? { ...locEntry, ...remEntry }
            : { ...remEntry, ...locEntry };
        }
        return { historique: hist };
      })(),
    };
    if (!merged.users     || merged.users.length     === 0) merged.users     = prev.users     || migrated.users     || [];
    if (!merged.adherents || merged.adherents.length === 0) merged.adherents = prev.adherents || [];
    // Dédup par numéro d'adhérent : garde le 1er (remote) en cas de doublon (UUID différent)
    const numSeen = new Set();
    merged.adherents = merged.adherents.filter(a => {
      if (!a.numero) return true;
      if (numSeen.has(a.numero)) return false;
      numSeen.add(a.numero); return true;
    });
    // Dédoublon sécurisé : éliminer les FANTÔMES en double (sans établissement, email, rib, tél)
    // Vrais adhérents homonymes (nom/prénom identiques mais personnes différentes) : tous conservés
    const normKey = a => ((a.nom || '') + '|' + (a.prenom || '')).trim().toUpperCase().replace(/\s+/g, ' ');
    const isPhantom = a => !a.etablissementId && !a.email && !a.rib && !a.telephone;
    const reals = merged.adherents.filter(a => !isPhantom(a));
    const phantoms = merged.adherents.filter(a => isPhantom(a));
    const phantomSeen = new Set(); const phantomDeduped = [];
    for (const a of phantoms) { const k = normKey(a); if (!phantomSeen.has(k)) { phantomSeen.add(k); phantomDeduped.push(a); } }
    const realNames = new Set(reals.map(normKey));
    merged.adherents = [...reals, ...phantomDeduped.filter(a => !realNames.has(normKey(a)))];
    // Dédoublon des établissements par nom normalisé (tous, qu'ils aient des adhérents ou non)
    const normEtabKey = e => ((e.nom || '')).trim().toUpperCase().replace(/\s+/g, ' ');
    // Choisir le canonique : celui avec le plus d'adhérents ; en cas d'égalité, le plus ancien (createdAt)
    const etabCounts = {};
    merged.adherents.forEach(a => { if (a.etablissementId) etabCounts[a.etablissementId] = (etabCounts[a.etablissementId] || 0) + 1; });
    const etabCanonMap = new Map(); // nom normalisé → établissement canonique
    for (const e of merged.etablissements) {
      if (deletedIds.has(e.id)) continue;
      const k = normEtabKey(e);
      const existing = etabCanonMap.get(k);
      if (!existing) { etabCanonMap.set(k, e); continue; }
      const eCount = etabCounts[e.id] || 0;
      const exCount = etabCounts[existing.id] || 0;
      if (eCount > exCount || (eCount === exCount && new Date(e.createdAt || 0) < new Date(existing.createdAt || 0))) {
        etabCanonMap.set(k, e); // e devient le canonique
      }
    }
    // Table de remapping : id doublon → id canonique
    const etabRemap = new Map();
    for (const e of merged.etablissements) {
      if (deletedIds.has(e.id)) continue;
      const canon = etabCanonMap.get(normEtabKey(e));
      if (canon && canon.id !== e.id) etabRemap.set(e.id, canon.id);
    }
    // Remapper les adhérents vers le canonique
    if (etabRemap.size > 0) {
      merged.adherents = merged.adherents.map(a =>
        a.etablissementId && etabRemap.has(a.etablissementId)
          ? { ...a, etablissementId: etabRemap.get(a.etablissementId) }
          : a
      );
    }
    merged.etablissements = [...etabCanonMap.values()];
    return merged;
  };

  // Sync localStorage
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(data)); } catch {}
  }, [data]);

  // Sauvegarde automatique quotidienne dans un second slot
  useEffect(() => {
    try {
      const existing = JSON.parse(localStorage.getItem(BACKUP_KEY) || 'null');
      const today = new Date().toISOString().slice(0, 10);
      if (!existing || existing.date !== today) {
        localStorage.setItem(BACKUP_KEY, JSON.stringify({
          date: today,
          savedAt: new Date().toISOString(),
          adherentsCount: (data.adherents || []).length,
          data,
        }));
      }
    } catch {}
  }, []); // une seule fois au démarrage (une sauvegarde par jour)

  // Load from Supabase on mount
  useEffect(() => {
    // Essaie les tables par entité d'abord (nouvelle archi), fallback vers blob
    loadEntitiesFromTables().then(async (tablesState) => {
      const remote = tablesState || await loadRemoteState();
      if (!remote) {
        applyingRemote.current = false;
        setRemoteLoading(false);
        return;
      }
      const migrated = migrateRemote(remote) || remote;
      applyingRemote.current = true;
      setData(prev => {
        const merged = smartMerge(migrated, prev, 0);
        minKnownUsers.current = Math.max(minKnownUsers.current, (merged.users || []).length);
        return merged;
      });
      setRemoteLoading(false);
    });
  }, []);  // eslint-disable-line

  // Auto-logout après inactivité
  useEffect(() => {
    if (!currentRole) { clearTimeout(logoutTimer.current); clearTimeout(warningTimer.current); return; }
    const events = ['mousemove','mousedown','keydown','touchstart','scroll','click'];
    const handler = () => resetTimers();
    events.forEach(e => window.addEventListener(e, handler, { passive: true }));
    resetTimers();
    return () => {
      events.forEach(e => window.removeEventListener(e, handler));
      clearTimeout(logoutTimer.current);
      clearTimeout(warningTimer.current);
    };
  }, [currentRole]); // eslint-disable-line

  // Toujours garder dataRef synchronisé pour beforeunload
  useEffect(() => { dataRef.current = data; }, [data]);

  // Sauvegarde d'urgence avant fermeture de la page
  // ⚠️  LIMITE NAVIGATEUR — voir REPORT.md §7 pour le détail complet.
  //   - beforeunload : requêtes fetch/XHR souvent interrompues sur Chrome 73+, Firefox, Safari.
  //   - pagehide : événement plus adapté aux navigations modernes / freeze d'onglets, mais non
  //     utilisé ici pour éviter la complexité (voir REPORT.md pour la justification).
  //   - Sur iOS Safari : ni beforeunload ni pagehide ne déclenchent de requête réseau fiable.
  //   - CE BLOC EST UN DERNIER FILET UNIQUEMENT. La vraie protection = debounce 300ms
  //     + immediateSync 0ms + suivi pendingOps/failedSyncOps actif.
  useEffect(() => {
    const handleUnload = () => { try { saveRemoteState(dataRef.current); } catch {} };
    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, []);

  // Debounced sync to Supabase on every data change (300ms au lieu de 2000ms)
  useEffect(() => {
    if (applyingRemote.current) {
      applyingRemote.current = false;
      // Rescue save : le merge distant vient d'annuler le timer de debounce en cours.
      // On replanifie une sauvegarde pour que les suppressions locales en attente atteignent Supabase.
      const t = setTimeout(() => {
        if ((dataRef.current.users || []).length >= minKnownUsers.current) {
          saveRemoteState(dataRef.current).then(ok => { if (ok) lastSavedAt.current = Date.now(); });
        }
      }, 1000);
      return () => clearTimeout(t);
    }
    // Guard : ne jamais écraser Supabase avec moins d'utilisateurs que ce qu'on connaît
    if ((data.users || []).length < minKnownUsers.current) return;
    minKnownUsers.current = Math.max(minKnownUsers.current, (data.users || []).length);
    setSyncStatus('syncing');
    const delay = immediateSync.current ? 0 : 300;
    immediateSync.current = false;
    const t = setTimeout(() => {
      // Essaie la sauvegarde par entité (delta), fallback vers blob si tables pas encore créées
      const prevSaved = savedStateRef.current;
      saveEntitiesToTables(data, prevSaved).then(entityOk => {
        if (!entityOk) return saveRemoteState(data); // fallback blob
        savedStateRef.current = data;
        return true;
      }).then(ok => {
        if (ok) {
          lastSavedAt.current = Date.now();
          pendingOps.current.clear();
          setPendingSyncCount(0);
          setFailedSyncOps([]);
          setSyncStatus('ok');
        } else {
          // Échec : dédoublonner les ops avant exposition dans l'UI
          // (plusieurs sauvegardes ratées peuvent contenir la même op logique)
          const failed = dedupeFailedOps([...pendingOps.current.values()]);
          setFailedSyncOps(prev => dedupeFailedOps([...prev, ...failed]));
          pendingOps.current.clear();
          setPendingSyncCount(0);
          setSyncStatus('error');
          setTimeout(() => setSyncStatus('ok'), 5000);
        }
      });
    }, delay);
    return () => clearTimeout(t);
  }, [data]);

  // Retry save when internet reconnects after a failure
  useEffect(() => {
    const handleOnline = () => {
      // Reload + merge before saving to avoid overwriting concurrent changes
      loadRemoteStateFull().then(full => {
        if (full) {
          const migrated = migrateRemote(full.state) || full.state;
          applyingRemote.current = true;
          lastPolledAt.current = full.updatedAt;
          setData(prev => {
            const merged = smartMerge(migrated, prev);
            minKnownUsers.current = Math.max(minKnownUsers.current, (merged.users || []).length);
            return merged;
          });
        } else if (syncStatus === 'error' || failedSyncOps.length > 0) {
          saveRemoteState(dataRef.current).then(ok => {
            if (ok) { lastSavedAt.current = Date.now(); setFailedSyncOps([]); setSyncStatus('ok'); }
          });
        }
      });
    };
    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [syncStatus, failedSyncOps]);

  // Polling fallback every 10s — works even without Supabase Replication enabled
  const lastPolledAt = useRef(null);
  useEffect(() => {
    const poll = async () => {
      const full = await loadRemoteStateFull();
      if (!full) return;
      if (full.updatedAt === lastPolledAt.current) return; // no change
      if (Date.now() - lastSavedAt.current < 15000) return; // our own recent save, skip (15s)
      lastPolledAt.current = full.updatedAt;
      const migrated = migrateRemote(full.state) || full.state;
      applyingRemote.current = true;
      setData(prev => {
        const merged = smartMerge(migrated, prev);
        minKnownUsers.current = Math.max(minKnownUsers.current, (merged.users || []).length);
        return merged;
      });
      setSyncStatus('remote');
      setTimeout(() => setSyncStatus('ok'), 3000);
    };
    const timer = setInterval(poll, 10000);
    return () => clearInterval(timer);
  }, []);

  // Realtime subscription — par entité (nouvelle archi) + fallback blob
  useEffect(() => {
    // Nouvelle archi : écoute les changements par table
    const unsubEntities = subscribeToEntities(({ entityKey, eventType, entity, deletedId }) => {
      applyingRemote.current = true;
      setData(prev => {
        if (!entityKey || !prev[entityKey]) return prev;
        const arr = prev[entityKey] || [];
        if (eventType === 'DELETE') {
          return { ...prev, [entityKey]: arr.filter(e => e.id !== deletedId) };
        }
        if (!entity) return prev;
        return { ...prev, [entityKey]: [...arr.filter(e => e.id !== entity.id), entity] };
      });
      setSyncStatus('remote');
      setTimeout(() => setSyncStatus('ok'), 3000);
    });
    // Fallback blob : écoute l'ancien canal pour compatibilité pendant la transition
    const unsubBlob = subscribeToState((remoteState) => {
      const migrated = migrateRemote(remoteState) || remoteState;
      applyingRemote.current = true;
      setData(prev => smartMerge(migrated, prev));
      setSyncStatus('remote');
      setTimeout(() => setSyncStatus('ok'), 3000);
    });
    return () => { unsubEntities(); unsubBlob(); };
  }, []);

  const addJournal = useCallback((action, detail) => {
    const entry = journalEntry(action, detail);
    setData(d => ({ ...d, journal: [entry, ...d.journal].slice(0, 200) }));
  }, []);

  // ── Adherent CRUD ──
  const saveAdherent = useCallback((adh) => {
    immediateSync.current = true; // sauvegarde Supabase immédiate (0ms) — opération critique
    trackOp('adhérent', adh.id ? 'modification' : 'création', `${adh.prenom} ${adh.nom}`);
    const adhId = adh.id;
    const adhWithTs = { ...adh, updatedAt: new Date().toISOString() };
    setData(d => {
      const exists = d.adherents.find(a => a.id === adhId);
      const adherents = exists
        ? d.adherents.map(a => a.id === adhId ? adhWithTs : a)
        : [...d.adherents, adhWithTs.id ? adhWithTs : { ...adhWithTs, id: uid() }];
      return { ...d, adherents };
    });
    addJournal('adherent', `${adh.prenom} ${adh.nom} — ${adh.id ? 'modifié' : 'créé'}`);
    // Upload nouveaux docs vers Supabase Storage (async arrière-plan)
    (adh.documents || []).filter(doc => doc.data && doc.data.startsWith('data:') && !doc.url)
      .forEach(doc => {
        uploadToStorage(`adherents/${adhId}/${doc.id}`, doc.data, doc.type).then(storagePath => {
          if (!storagePath) return;
          const url = getStoragePublicUrl(storagePath);
          setData(prev => ({
            ...prev,
            adherents: prev.adherents.map(a => a.id !== adhId ? a : {
              ...a,
              documents: (a.documents || []).map(d => d.id !== doc.id ? d : { ...d, url, storagePath, data: undefined }),
            }),
          }));
        });
      });
  }, [addJournal]);

  const deleteAdherent = useCallback((id) => {
    immediateSync.current = true; // sauvegarde Supabase immédiate (0ms) — opération critique
    const adhName = (dataRef.current.adherents || []).find(a => a.id === id);
    trackOp('adhérent', 'suppression', adhName ? `${adhName.prenom} ${adhName.nom}` : id);
    setData(d => {
      const adh = d.adherents.find(a => a.id === id);
      if (adh) {
        const paths = (adh.documents || []).map(doc => `adherents/${id}/${doc.id}`);
        if (paths.length) deleteFromStorage(paths);
      }
      return {
        ...d,
        adherents: d.adherents.filter(a => a.id !== id),
        _deletedIds: [...new Set([...(d._deletedIds || []), id])],
      };
    });
    addJournal('adherent', `Adhérent supprimé (id: ${id})`);
  }, [addJournal]);

  // ── Etablissement CRUD ──
  const saveEtablissement = useCallback((etab) => {
    immediateSync.current = true;
    trackOp('établissement', etab.id ? 'modification' : 'création', etab.nom);
    setData(d => {
      const exists = d.etablissements.find(e => e.id === etab.id);
      const etablissements = exists
        ? d.etablissements.map(e => e.id === etab.id ? etab : e)
        : [...d.etablissements, { ...etab, id: uid() }];
      return { ...d, etablissements };
    });
    addJournal('etablissement', `${etab.nom} — ${etab.id ? 'modifié' : 'créé'}`);
  }, [addJournal]);

  const deleteEtablissement = useCallback((id) => {
    immediateSync.current = true;
    const etabName = (dataRef.current.etablissements || []).find(e => e.id === id);
    trackOp('établissement', 'suppression', etabName?.nom || id);
    setData(d => ({
      ...d,
      etablissements: d.etablissements.filter(e => e.id !== id),
      _deletedIds: [...new Set([...(d._deletedIds || []), id])],
    }));
    addJournal('etablissement', `Établissement supprimé (id: ${id})`);
  }, [addJournal]);

  // ── Cotisation update ──
  const setCotisation = useCallback((year, adhId, month, status, motif = '') => {
    setData(d => {
      const cots = { ...d.cotisations };
      if (!cots[year]) cots[year] = {};
      if (!cots[year][adhId]) cots[year][adhId] = {};
      if (status === 'non_pointe') {
        const adhCots = { ...cots[year][adhId] };
        delete adhCots[month];
        cots[year][adhId] = adhCots;
      } else {
        cots[year][adhId][month] = { status, motif };
      }
      return { ...d, cotisations: cots };
    });
    addJournal('cotisation', `${year}/${String(month).padStart(2,'0')} — adh ${adhId} → ${status}${motif ? ' ('+motif+')' : ''}`);
  }, [addJournal]);

  const resetAllCotisationsYear = useCallback((year) => {
    setData(d => {
      const cots = { ...d.cotisations };
      cots[year] = {};
      return { ...d, cotisations: cots };
    });
    addJournal('cotisation', `Réinitialisation complète des pointages ${year}`);
  }, [addJournal]);

  // ── Seed data (base réelle adhérents) ──
  const loadSeedData = useCallback(() => {
    setData(d => ({
      ...d,
      adherents: SEED_ADHERENTS,
      journal: [journalEntry('import', `Base initiale chargée : ${SEED_ADHERENTS.length} adhérents`), ...(d.journal || [])],
    }));
  }, []);

  // ── Import virement 2023 (dédoublonnage nom+prénom) ──
  const loadVirement = useCallback(() => {
    setData(d => {
      const normalize = s => (s || '').trim().toUpperCase().replace(/\s+/g, ' ');
      const existing = new Set(d.adherents.map(a => normalize(a.nom) + '|' + normalize(a.prenom)));
      let etabs = [...d.etablissements];
      const getOrCreateEtab = (nom) => {
        if (!nom) return '';
        const found = etabs.find(e => normalize(e.nom) === normalize(nom));
        if (found) return found.id;
        const newEtab = { id: uid(), nom: nom.trim() };
        etabs.push(newEtab);
        return newEtab.id;
      };
      let added = 0;
      let adherents = [...d.adherents];
      const year = new Date().getFullYear();
      SEED_VIREMENT.forEach(p => {
        const key = normalize(p.nom) + '|' + normalize(p.prenom);
        if (existing.has(key)) return;
        existing.add(key);
        added++;
        const seq = String(adherents.length + 1).padStart(3, '0');
        adherents.push({
          id: uid(),
          prenom: p.prenom,
          nom: p.nom,
          numero: `FO-${year}-${seq}`,
          statut: 'Actif',
          etablissementId: getOrCreateEtab(p.etab),
          metier: '',
          contrat: 'CDI',
          dateEntree: '',
          convention: 'CCN 51',
          coefficient: '',
          dateAdhesion: '',
          modePaiement: 'Virement',
          jourPrelevement: '',
          periodicite: 'Mensuelle',
          montant: p.montant || 0,
          rib: '',
          telephone: '',
          email: '',
          adresse: '',
          notes: '',
          delegueNom: '',
          createdBy: 'Import Virement 2023',
        });
      });
      const entry = journalEntry('import', `Virement 2023 : ${added} adhérent(s) ajouté(s), ${SEED_VIREMENT.length - added} doublon(s) ignoré(s)`);
      return { ...d, adherents, etablissements: etabs, journal: [entry, ...d.journal].slice(0, 200) };
    });
  }, []);

  // ── Demo data ──
  const loadDemo = useCallback(() => {
    const cotis = generateDemoCotisations();
    setData(d => ({
      ...d,
      adherents: DEMO_ADHERENTS,
      etablissements: DEMO_ETABLISSEMENTS,
      cotisations: cotis,
      solde: 3240.50,
      journal: [journalEntry('demo', 'Données de démonstration chargées'), ...(d.journal || [])],
    }));
  }, []);

  // ── Year ──
  const setYear = useCallback((y) => setData(d => ({ ...d, selectedYear: y })), []);
  const setSolde = useCallback((s) => setData(d => ({ ...d, solde: s })), []);

  // ── User management ──
  const saveUser = useCallback((user) => {
    immediateSync.current = true;
    trackOp('utilisateur', user.id ? 'modification' : 'création', user.username);
    setData(d => {
      const exists = (d.users || []).find(u => u.id === user.id);
      const users = exists
        ? d.users.map(u => u.id === user.id ? user : u)
        : [...(d.users || []), { ...user, id: uid(), createdAt: new Date().toISOString() }];
      return { ...d, users };
    });
    addJournal('securite', `Utilisateur ${user.username} ${user.id ? 'modifié' : 'créé'}`);
  }, [addJournal]);

  const deleteUser = useCallback((id) => {
    immediateSync.current = true;
    minKnownUsers.current = Math.max(1, minKnownUsers.current - 1); // suppression intentionnelle : autoriser la sauvegarde
    const userName = (dataRef.current.users || []).find(u => u.id === id);
    trackOp('utilisateur', 'suppression', userName?.username || id);
    setData(d => ({
      ...d,
      users: (d.users || []).filter(u => u.id !== id),
      _deletedIds: [...new Set([...(d._deletedIds || []), id])],
    }));
    addJournal('securite', `Utilisateur supprimé (id: ${id})`);
  }, [addJournal]);

  // ── Navigate ──
  const goToAdherent = useCallback((id) => { setSelectedAdherentId(id); setPage('adherents'); }, []);
  const goToEtab = useCallback((id) => { setSelectedEtabId(id); setPage('etablissements'); }, []);

  // ── Conseil Syndical CRUD ──
  const saveConseilMember = useCallback((m) => {
    setData(d => {
      const exists = d.conseilMembers.find(x => x.id === m.id);
      const conseilMembers = exists
        ? d.conseilMembers.map(x => x.id === m.id ? m : x)
        : [...d.conseilMembers, { ...m, id: uid() }];
      return { ...d, conseilMembers };
    });
  }, []);

  const deleteConseilMember = useCallback((id) => {
    setData(d => ({ ...d, conseilMembers: d.conseilMembers.filter(x => x.id !== id) }));
  }, []);

  const addConvocation = useCallback((conv) => {
    setData(d => ({ ...d, convocations: [...d.convocations, conv] }));
    addJournal('convocation', `${conv.type.toUpperCase()} — ${conv.memberName}`);
  }, [addJournal]);

  const deleteConvocationsForMember = useCallback((memberId, year) => {
    setData(d => ({
      ...d,
      convocations: d.convocations.filter(c =>
        !(c.memberId === memberId && new Date(c.createdAt).getFullYear() === year)
      ),
    }));
  }, []);

  const resetConvocationsForYear = useCallback((year) => {
    setData(d => ({
      ...d,
      convocations: d.convocations.filter(c => new Date(c.createdAt).getFullYear() !== year),
    }));
  }, []);

  // ── Demandes CRUD ──
  const saveDemande = useCallback((dem) => {
    immediateSync.current = true;
    const demId = dem.id;
    setData(d => {
      const exists = d.demandes.find(x => x.id === demId);
      const demandes = exists ? d.demandes.map(x => x.id === demId ? dem : x) : [...d.demandes, dem];
      return { ...d, demandes };
    });
    // Upload docs vers Storage (async)
    if (dem.document && typeof dem.document === 'string' && dem.document.startsWith('data:')) {
      uploadToStorage(`demandes/${demId}/main`, dem.document, 'application/pdf').then(storagePath => {
        if (storagePath) setData(prev => ({ ...prev, demandes: prev.demandes.map(d => d.id !== demId ? d : { ...d, document: getStoragePublicUrl(storagePath) }) }));
      });
    }
    if (dem.sepaDocument?.data && dem.sepaDocument.data.startsWith('data:') && !dem.sepaDocument.url) {
      uploadToStorage(`demandes/${demId}/sepa`, dem.sepaDocument.data, dem.sepaDocument.type).then(storagePath => {
        if (storagePath) setData(prev => ({ ...prev, demandes: prev.demandes.map(d => d.id !== demId ? d : { ...d, sepaDocument: { ...d.sepaDocument, url: getStoragePublicUrl(storagePath), data: undefined } }) }));
      });
    }
    if (dem.complementDocument?.data && dem.complementDocument.data.startsWith('data:') && !dem.complementDocument.url) {
      uploadToStorage(`demandes/${demId}/comp`, dem.complementDocument.data, dem.complementDocument.type).then(storagePath => {
        if (storagePath) setData(prev => ({ ...prev, demandes: prev.demandes.map(d => d.id !== demId ? d : { ...d, complementDocument: { ...d.complementDocument, url: getStoragePublicUrl(storagePath), data: undefined } }) }));
      });
    }
  }, []);

  const validateDemande = useCallback((id) => {
    immediateSync.current = true;
    setData(d => {
      const dem = d.demandes.find(x => x.id === id);
      if (!dem || dem.statut === 'valide') return d;
      const demandes = d.demandes.map(x => x.id === id ? { ...x, statut: 'valide', dateValidation: new Date().toISOString() } : x);

      if (dem.type === 'arret_adhesion') {
        const adherents = d.adherents.map(a => a.id === dem.adherentId ? { ...a, statut: 'Inactif' } : a);
        const entry = journalEntry('validation', `Arrêt adhésion validé : ${dem.adherentPrenom} ${dem.adherentNom}`);
        return { ...d, adherents, demandes, journal: [entry, ...d.journal].slice(0, 200) };
      }
      if (dem.type === 'changement_rib') {
        const adherents = d.adherents.map(a => {
          if (a.id !== dem.adherentId) return a;
          const docs = dem.ribDocument
            ? [...(a.documents || []), { id: uid(), nom: dem.ribDocument.name || dem.ribDocument.nom || 'RIB', type: dem.ribDocument.type, label: 'RIB',
                ...(dem.ribDocument.url ? { url: dem.ribDocument.url } : { data: dem.ribDocument.data }),
                dateAjout: new Date().toISOString() }]
            : (a.documents || []);
          return { ...a, rib: dem.newIban || a.rib, bic: dem.newBic || a.bic, documents: docs };
        });
        const entry = journalEntry('validation', `Changement RIB validé : ${dem.adherentPrenom} ${dem.adherentNom}`);
        return { ...d, adherents, demandes, journal: [entry, ...d.journal].slice(0, 200) };
      }
      if (dem.type === 'bascule_prelevement') {
        const adherents = d.adherents.map(a => {
          if (a.id !== dem.adherentId) return a;
          const docs = dem.sepaDocument
            ? [...(a.documents||[]), { id: uid(), nom: dem.sepaDocument.nom || 'Mandat SEPA', type: dem.sepaDocument.type,
                ...(dem.sepaDocument.url ? { url: dem.sepaDocument.url } : { data: dem.sepaDocument.data }),
                dateAjout: new Date().toISOString() }]
            : (a.documents || []);
          return { ...a, modePaiement: 'Prélèvement auto', rib: dem.newIban || a.rib, bic: dem.newBic || a.bic,
            rum: dem.rum || a.rum, titulaireCompte: dem.titulaire || a.titulaireCompte,
            jourPrelevement: dem.jourPrelevement || a.jourPrelevement,
            periodicite: dem.periodicite || a.periodicite, documents: docs };
        });
        const entry = journalEntry('validation', `Bascule prélèvement validée : ${dem.adherentPrenom} ${dem.adherentNom}`);
        return { ...d, adherents, demandes, journal: [entry, ...d.journal].slice(0, 200) };
      }

      const year = dem.dateAdhesion ? new Date(dem.dateAdhesion).getFullYear() : new Date().getFullYear();
      const seq = String(d.adherents.length + 1).padStart(3, '0');
      const newAdh = {
        id: uid(), prenom: dem.prenom, nom: dem.nom,
        genre: dem.civilite === 'Mme' ? 'Madame' : dem.civilite === 'M.' ? 'Monsieur' : '',
        numero: `FO-${year}-${seq}`, statut: 'Actif',
        createdAt: new Date().toISOString(),
        etablissementId: dem.etablissementId || '', metier: dem.metier || '',
        contrat: dem.contrat || 'CDI', dateEntree: dem.dateEntree || '',
        convention: dem.convention || 'CCN 51', coefficient: dem.coefficient || '',
        dateAdhesion: dem.dateSignature || new Date().toISOString().slice(0,10),
        modePaiement: dem.modePaiement || (dem.iban ? 'Prélèvement auto' : 'Chèque'),
        jourPrelevement: dem.jourPrelevement || '5',
        periodicite: dem.periodicite || 'Mensuelle',
        montant: dem.montant || 0, rib: dem.iban || '', bic: dem.bic || '',
        telephone: dem.telephone || '', email: dem.email || '',
        adresse: [dem.adresse, dem.codePostal, dem.ville].filter(Boolean).join(', '),
        notes: `RUM: ${dem.rum || ''} | Banque: ${dem.banque || ''}`,
        documents: [
          dem.document ? { id: uid(),
            nom:  dem.document.name || dem.document.nom || 'Mandat SEPA',
            type: dem.document.type || 'application/pdf',
            label: 'Mandat SEPA',
            ...(dem.document.url  ? { url:  dem.document.url  }
              : dem.document.data ? { data: dem.document.data }
              : typeof dem.document === 'string' && dem.document.startsWith('data:') ? { data: dem.document } : {}),
            dateAjout: new Date().toISOString() } : null,
          dem.documentRib ? { id: uid(),
            nom:  dem.documentRib.name || dem.documentRib.nom || 'RIB',
            type: dem.documentRib.type || 'application/pdf',
            label: 'RIB',
            ...(dem.documentRib.url  ? { url:  dem.documentRib.url  }
              : dem.documentRib.data ? { data: dem.documentRib.data } : {}),
            dateAjout: new Date().toISOString() } : null,
          dem.complementDocument ? { id: uid(),
            nom:  dem.complementDocument.nom || 'Document complément',
            type: dem.complementDocument.type,
            label: 'Complément',
            ...(dem.complementDocument.url ? { url: dem.complementDocument.url } : { data: dem.complementDocument.data }),
            dateAjout: new Date().toISOString() } : null,
        ].filter(Boolean),
      };
      const entry = journalEntry('validation', `Adhésion validée : ${dem.prenom} ${dem.nom}`);
      return { ...d, adherents: [...d.adherents, newAdh], demandes, journal: [entry, ...d.journal].slice(0, 200) };
    });
  }, []);

  const recreerAdherentDemande = useCallback((id) => {
    immediateSync.current = true;
    setData(d => {
      const dem = d.demandes.find(x => x.id === id);
      if (!dem || (dem.type && dem.type !== 'adhesion')) return d;
      const alreadyExists = d.adherents.some(a =>
        a.prenom?.toLowerCase() === dem.prenom?.toLowerCase() &&
        a.nom?.toLowerCase() === dem.nom?.toLowerCase()
      );
      if (alreadyExists) return d;
      const year = dem.dateAdhesion ? new Date(dem.dateAdhesion).getFullYear() : new Date().getFullYear();
      const seq = String(d.adherents.length + 1).padStart(3, '0');
      const newAdh = {
        id: uid(), prenom: dem.prenom, nom: dem.nom,
        genre: dem.civilite === 'Mme' ? 'Madame' : dem.civilite === 'M.' ? 'Monsieur' : '',
        numero: `FO-${year}-${seq}`, statut: 'Actif',
        createdAt: new Date().toISOString(),
        etablissementId: dem.etablissementId || '', metier: dem.metier || '',
        contrat: dem.contrat || 'CDI', dateEntree: dem.dateEntree || '',
        convention: dem.convention || 'CCN 51', coefficient: dem.coefficient || '',
        dateAdhesion: dem.dateSignature || new Date().toISOString().slice(0,10),
        modePaiement: dem.modePaiement || (dem.iban ? 'Prélèvement auto' : 'Chèque'),
        jourPrelevement: dem.jourPrelevement || '5',
        periodicite: dem.periodicite || 'Mensuelle',
        montant: dem.montant || 0, rib: dem.iban || '', bic: dem.bic || '',
        telephone: dem.telephone || '', email: dem.email || '',
        adresse: [dem.adresse, dem.codePostal, dem.ville].filter(Boolean).join(', '),
        notes: `RUM: ${dem.rum || ''} | Banque: ${dem.banque || ''}`,
        documents: [
          dem.document ? { id: uid(), nom: 'Document adhésion',
            type: (typeof dem.document === 'string' && dem.document.startsWith('data:')) ? dem.document.split(';')[0].replace('data:','') : 'application/pdf',
            ...(typeof dem.document === 'string' && dem.document.startsWith('data:') ? { data: dem.document } : { url: dem.document }),
            dateAjout: new Date().toISOString() } : null,
        ].filter(Boolean),
      };
      const entry = journalEntry('validation', `Fiche recréée (récupération) : ${dem.prenom} ${dem.nom}`);
      return { ...d, adherents: [...d.adherents, newAdh], journal: [entry, ...d.journal].slice(0, 200) };
    });
  }, []);

  const rejectDemande = useCallback((id, motif) => {
    immediateSync.current = true;
    setData(d => ({
      ...d,
      demandes: d.demandes.map(x => x.id === id ? { ...x, statut: 'refuse', motifRefus: motif, dateTraitement: new Date().toISOString() } : x),
    }));
    addJournal('validation', `Adhésion refusée (id: ${id}) — ${motif}`);
  }, [addJournal]);

  const askComplement = useCallback((id, message) => {
    immediateSync.current = true;
    setData(d => ({
      ...d,
      demandes: d.demandes.map(x => x.id === id ? { ...x, statut: 'complement', messageComplement: message, dateTraitement: new Date().toISOString() } : x),
    }));
    addJournal('validation', `Complément demandé (id: ${id})`);
  }, [addJournal]);

  const submitComplementResponse = useCallback((id, response, docFile) => {
    immediateSync.current = true;
    setData(d => ({
      ...d,
      demandes: d.demandes.map(x => x.id === id ? { ...x, statut: 'en_attente', complementResponse: response, complementDocument: docFile || null, dateComplementResponse: new Date().toISOString() } : x),
    }));
    addJournal('adhesion', `Complément soumis (id: ${id})`);
    if (docFile?.data && docFile.data.startsWith('data:') && !docFile.url) {
      uploadToStorage(`demandes/${id}/comp`, docFile.data, docFile.type).then(storagePath => {
        if (storagePath) setData(prev => ({ ...prev, demandes: prev.demandes.map(d => d.id !== id ? d : { ...d, complementDocument: { ...(d.complementDocument || {}), url: getStoragePublicUrl(storagePath), data: undefined } }) }));
      });
    }
  }, [addJournal]);

  const deleteDemande = useCallback((id) => {
    deleteFromStorage([`demandes/${id}/main`, `demandes/${id}/sepa`, `demandes/${id}/comp`]);
    setData(d => ({ ...d, demandes: d.demandes.filter(x => x.id !== id) }));
    addJournal('validation', `Demande supprimée (id: ${id})`);
  }, [addJournal]);

  const saveDepense = useCallback((dep) => {
    immediateSync.current = true; // sauvegarde Supabase immédiate — opération critique
    trackOp('dépense', dep.id ? 'modification' : 'création', dep.motif);
    setData(d => {
      const idx = (d.depenses || []).findIndex(x => x.id === dep.id);
      const depenses = idx >= 0
        ? d.depenses.map(x => x.id === dep.id ? { ...x, ...dep } : x)
        : [...(d.depenses || []), { createdAt: new Date().toISOString(), ...dep }];
      return { ...d, depenses };
    });
  }, []); // eslint-disable-line

  const deleteDepense = useCallback((id) => {
    immediateSync.current = true; // sauvegarde Supabase immédiate — opération critique
    const dep = (dataRef.current.depenses || []).find(x => x.id === id);
    // Supprime le fichier Storage associé si présent (justificatif migré vers Storage)
    if (dep?.justificatif?.storagePath) deleteFromStorage([dep.justificatif.storagePath]);
    trackOp('dépense', 'suppression', dep?.motif || id);
    setData(d => ({ ...d, depenses: (d.depenses || []).filter(x => x.id !== id) }));
  }, []); // eslint-disable-line

  const saveComptaOperation = useCallback((year, month, op) => {
    setData(d => {
      const compta = { ...(d.comptabilite || {}) };
      if (!compta[year]) compta[year] = {};
      if (!compta[year][month]) compta[year][month] = { operations: [], soldeReleve: null, soldeOuverture: null };
      const ops = compta[year][month].operations || [];
      const exists = ops.findIndex(o => o.id === op.id);
      compta[year][month] = { ...compta[year][month],
        operations: exists >= 0 ? ops.map(o => o.id === op.id ? op : o) : [...ops, op] };
      return { ...d, comptabilite: compta };
    });
  }, []);

  const deleteComptaOperation = useCallback((year, month, opId) => {
    setData(d => {
      const compta = { ...(d.comptabilite || {}) };
      if (compta[year]?.[month]) {
        compta[year][month] = { ...compta[year][month],
          operations: (compta[year][month].operations || []).filter(o => o.id !== opId) };
      }
      return { ...d, comptabilite: compta };
    });
  }, []);

  const setComptaMoisData = useCallback((year, month, patch) => {
    setData(d => {
      const compta = { ...(d.comptabilite || {}) };
      if (!compta[year]) compta[year] = {};
      if (!compta[year][month]) compta[year][month] = { operations: [], soldeReleve: null, soldeOuverture: null };
      compta[year][month] = { ...compta[year][month], ...patch };
      return { ...d, comptabilite: compta };
    });
  }, []);

  const clearComptaMoisOperations = useCallback((year, month) => {
    setData(d => {
      const compta = { ...(d.comptabilite || {}) };
      if (compta[year]?.[month]) {
        compta[year][month] = { ...compta[year][month], operations: [] };
      }
      return { ...d, comptabilite: compta };
    });
  }, []);

  const saveBudget = useCallback((year, montant) => {
    immediateSync.current = true;
    setData(d => ({ ...d, budgetDepenses: { ...(d.budgetDepenses || {}), [year]: montant } }));
  }, []);

  const saveCompteResultat = useCallback((patch) => {
    setData(d => ({ ...d, compteResultat: { ...(d.compteResultat || {}), ...patch } }));
  }, []);

  const exportBackup = useCallback(() => {
    const json = JSON.stringify(data, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const date = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `sdassp-fo-sauvegarde-${date}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [data]);

  const importBackup = useCallback((file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const parsed = JSON.parse(e.target.result);
        if (!parsed.adherents || !parsed.cotisations) {
          alert('Fichier invalide : ce n\'est pas une sauvegarde SDASSP FO.');
          return;
        }
        if (!window.confirm(`Restaurer la sauvegarde ?\nCela remplacera toutes les données actuelles par ${parsed.adherents?.length || 0} adhérents.`)) return;
        setData(parsed);
        immediateSync.current = true;
      } catch {
        alert('Impossible de lire ce fichier. Vérifiez qu\'il s\'agit bien d\'une sauvegarde SDASSP FO.');
      }
    };
    reader.readAsText(file);
  }, []);

  // ── Login check ──
  if (currentRole === null) {
    return <PinLogin
      users={data.users || []}
      remoteLoading={remoteLoading}
      saveUser={saveUser}
      onLogin={(role, title, admin, username) => {
        setCurrentRole(role);
        setCurrentRoleTitle(title);
        setPage(role === 'delegue' ? 'accueil_ds' : role === 'bureau_simple' ? 'depenses' : 'dashboard');
        setIsAdmin(admin || false);
        setCurrentUsername(username || '');
        try { sessionStorage.setItem(SESSION_KEY, JSON.stringify({ role, roleTitle: title, isAdmin: admin||false, username: username||'', expiresAt: Date.now() + SESSION_TTL })); } catch {}
        const ua = navigator.userAgent;
        const device = /iPhone|iPad|iPod/i.test(ua) ? '📱 iOS' : /Android/i.test(ua) ? '📱 Android' : /Mobile/i.test(ua) ? '📱 Mobile' : '🖥️ Ordinateur';
        const browser = /Chrome/i.test(ua) && !/Edge/i.test(ua) ? 'Chrome' : /Safari/i.test(ua) ? 'Safari' : /Firefox/i.test(ua) ? 'Firefox' : /Edge/i.test(ua) ? 'Edge' : 'Navigateur';
        const connexionEntry = { id: uid(), username: username||'', role, roleTitle: title, ts: new Date().toISOString(), device, browser };
        setData(d => ({ ...d, connexions: [connexionEntry, ...(d.connexions||[])].slice(0,100) }));
      }}
    />;
  }

  const pendingCount = (data.demandes || []).filter(d => d.statut === 'en_attente').length;
  const upcomingConvocCount = (data.convocations || []).filter(c => c.dateReunion && new Date(c.dateReunion) >= new Date()).length;
  const currentUser = (data.users || []).find(u => u.username === currentUsername);
  const currentUserEtabsIds = currentUser?.etablissementsIds || [];
  const visiblePages = ALL_PAGES.filter(p => p.roles.includes(currentRole));

  const sharedProps = {
    adherents: data.adherents,
    etablissements: data.etablissements,
    cotisations: data.cotisations,
    selectedYear: data.selectedYear,
    solde: data.solde,
    journal: data.journal,
    demandes: data.demandes || [],
    currentRole,
    currentUsername,
    saveAdherent,
    deleteAdherent,
    saveEtablissement,
    deleteEtablissement,
    setCotisation,
    resetAllCotisationsYear,
    setYear,
    setSolde,
    loadDemo,
    goToAdherent,
    goToEtab,
    addJournal,
    saveDemande,
    validateDemande,
    recreerAdherentDemande,
    rejectDemande,
    askComplement,
    submitComplementResponse,
    deleteDemande,
    setPage,
    conseilMembers: data.conseilMembers || MEMBRES_INIT,
    convocations: data.convocations || [],
    saveConseilMember,
    deleteConseilMember,
    addConvocation,
    deleteConvocationsForMember,
    resetConvocationsForYear,
    currentUserEmail: (data.users || []).find(u => u.username === currentUsername)?.email || '',
    users: data.users || [],
    currentUserEtabsIds,
    comptabilite: data.comptabilite || {},
    saveComptaOperation,
    deleteComptaOperation,
    setComptaMoisData,
    clearComptaMoisOperations,
    compteResultat: data.compteResultat || { historique: {} },
    saveCompteResultat,
  };

  const handleNavClick = (pid) => {
    setPage(pid);
    setSidebarOpen(false);
    if (pid !== 'adherents') setSelectedAdherentId(null);
    if (pid !== 'etablissements') setSelectedEtabId(null);
  };

  return (
    <div className="app-layout">
      {/* Modal avertissement déconnexion auto */}
      {showTimeoutWarning && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
          <div style={{ background:'#fff', borderRadius:14, padding:28, maxWidth:360, width:'100%', textAlign:'center', boxShadow:'0 8px 32px rgba(0,0,0,0.2)' }}>
            <div style={{ fontSize:40, marginBottom:12 }}>⏳</div>
            <div style={{ fontWeight:700, fontSize:17, marginBottom:8 }}>Session sur le point d'expirer</div>
            <div style={{ fontSize:14, color:'#666', marginBottom:20 }}>Vous serez déconnecté dans <strong>1 minute</strong> pour inactivité.</div>
            <div style={{ display:'flex', gap:10, justifyContent:'center' }}>
              <button className="btn btn-outline" onClick={doLogout}>Se déconnecter</button>
              <button className="btn btn-primary" onClick={resetTimers}>Rester connecté</button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile overlay */}
      {sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />}

      {/* Sidebar */}
      <aside className={`sidebar${sidebarOpen ? ' open' : ''}`}>
        <div className="sidebar-brand">
          <div className="sidebar-brand-logo"><FoLogo size={38} /></div>
          <div className="sidebar-brand-text">
            SDASSP FO Réunion
            <small>Gestion syndicale</small>
          </div>
        </div>

        <div className="sidebar-section-label">Navigation</div>
        <nav className="sidebar-nav">
          {visiblePages.map(p => {
            const badgeCount = p.badge === 'demandes' ? pendingCount : p.badge === 'convocations' ? upcomingConvocCount : 0;
            return (
              <button key={p.id} className={`sidebar-nav-item${page === p.id ? ' active' : ''}`} onClick={() => handleNavClick(p.id)}>
                <span className="sidebar-nav-icon">{p.icon}</span>
                <span className="sidebar-nav-label">{p.label}</span>
                {badgeCount > 0 && <span className="sidebar-nav-badge">{badgeCount}</span>}
              </button>
            );
          })}
        </nav>

        <div className="sidebar-footer">
          <div className="sidebar-year-label">Année</div>
          <div className="sidebar-year-row">
            {[2025, 2026, 2027, 2028].map(y => (
              <button key={y} className={`sidebar-year-btn${data.selectedYear === y ? ' active' : ''}`} onClick={() => setYear(y)}>{y}</button>
            ))}
          </div>
          {/* Sélecteur année compact pour tablette (sidebar icon-only) */}
          <div className="sidebar-year-compact">
            <select
              value={data.selectedYear}
              onChange={e => setYear(Number(e.target.value))}
              style={{ width:'100%', fontSize:13, padding:'8px 4px', borderRadius:8, border:'1px solid rgba(255,255,255,.35)', background:'rgba(255,255,255,.15)', color:'#fff', cursor:'pointer', textAlign:'center', fontWeight:700 }}
            >
              {[2025, 2026, 2027, 2028].map(y => <option key={y} value={y} style={{ color:'#111' }}>{y}</option>)}
            </select>
          </div>
          <div className="sidebar-role-pill">
            <span>{currentRole === 'tresorier' ? '🔐' : '👤'}</span>
            <span>{currentRoleTitle}</span>
          </div>
          <div style={{
            fontSize: 11, padding: '4px 10px', borderRadius: 8, marginBottom: 4,
            display: 'flex', alignItems: 'center', gap: 6,
            background: syncStatus === 'remote' ? 'rgba(22,163,74,.18)' : syncStatus === 'error' ? 'rgba(220,38,38,.25)' : syncStatus === 'syncing' ? 'rgba(255,255,255,.08)' : 'rgba(255,255,255,.06)',
            color: syncStatus === 'remote' ? '#86efac' : syncStatus === 'error' ? '#fca5a5' : '#aaa',
          }}>
            <span style={{ fontSize: 9, animation: syncStatus === 'syncing' ? 'spin 1s linear infinite' : 'none' }}>
              {syncStatus === 'remote' ? '🟢' : syncStatus === 'error' ? '⚠️' : syncStatus === 'syncing' ? '🔄' : '☁️'}
            </span>
            <span>
              {syncStatus === 'remote' ? 'Mis à jour par un collègue' : syncStatus === 'error' ? 'Erreur sync Supabase !' : syncStatus === 'syncing' ? 'Enregistrement...' : 'Synchronisé'}
            </span>
          </div>
          {/* Indicateur d'opérations critiques en attente ou en échec de sync */}
          {(pendingSyncCount > 0 || failedSyncOps.length > 0) && (
            <div style={{
              fontSize: 10, padding: '3px 10px', borderRadius: 6, marginBottom: 4,
              background: failedSyncOps.length > 0 ? 'rgba(220,38,38,.2)' : 'rgba(255,200,0,.12)',
              color: failedSyncOps.length > 0 ? '#fca5a5' : '#fde68a',
              lineHeight: 1.4,
            }}>
              {failedSyncOps.length > 0 ? (
                <>
                  <div>🔴 {failedSyncOps.length} opération(s) non sauvegardée(s)</div>
                  <button
                    onClick={retrySync}
                    disabled={syncStatus === 'syncing'}
                    style={{
                      marginTop: 4, width: '100%', padding: '3px 0',
                      background: syncStatus === 'syncing' ? 'rgba(255,255,255,.1)' : 'rgba(220,38,38,.5)',
                      color: '#fff', border: 'none', borderRadius: 4,
                      fontSize: 10, cursor: syncStatus === 'syncing' ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {syncStatus === 'syncing' ? '🔄 Réessai en cours...' : '🔁 Réessayer la sauvegarde'}
                  </button>
                </>
              ) : (
                `⏳ ${pendingSyncCount} opération(s) en attente de confirmation...`
              )}
            </div>
          )}
          {isAdmin && (
            <button className="sidebar-action-btn admin" onClick={() => setShowSettings(true)}
              title="Gérer les utilisateurs, accès et données">
              <span>🔐</span><span>Administration</span>
            </button>
          )}
          <button className="sidebar-action-btn settings" onClick={() => setShowSettings(true)}
            title="Paramètres de l'application">
            <span>⚙️</span><span>Paramètres</span>
          </button>
          {pwaPrompt && !pwaInstalled && (
            <button className="sidebar-action-btn" onClick={() => { pwaPrompt.prompt(); pwaPrompt.userChoice.then(() => setPwaPrompt(null)); }}
              title="Installer l'application sur votre téléphone ou ordinateur"
              style={{ background:'linear-gradient(135deg,#1565c0,#1976d2)', color:'#fff', fontWeight:700 }}>
              <span>📲</span><span>Installer l'appli</span>
            </button>
          )}
          {pwaInstalled && (
            <div style={{ fontSize:11, color:'#4caf50', textAlign:'center', padding:'4px 0', fontWeight:600 }}>✅ Application installée</div>
          )}
          <button className="sidebar-action-btn logout"
            onClick={doLogout}
            title="Se déconnecter et verrouiller l'application">
            <span>🔒</span><span>Se déconnecter</span>
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="main-content">
        {/* Mobile top bar */}
        <div className="mobile-topbar">
          <button className="hamburger-btn" onClick={() => setSidebarOpen(o => !o)} aria-label="Menu">
            <span /><span /><span />
          </button>
          <span className="mobile-topbar-title">SDASSP FO Réunion</span>
          {pendingCount > 0 && <span className="mobile-badge-alert">{pendingCount}</span>}
          <button
            className="mobile-logout-btn"
            onClick={doLogout}
            title="Se déconnecter"
            aria-label="Se déconnecter"
          >🔓</button>
        </div>
        {page === 'dashboard'   && <Dashboard {...sharedProps} setPage={setPage} pendingCount={pendingCount} />}
        {page === 'accueil_ds'  && <DashboardDS {...sharedProps} />}
        {page === 'pointage' && <Pointage {...sharedProps} />}
        {page === 'pointage_releve' && <PointageReleve {...sharedProps} />}
        {page === 'adherents' && (
          <FicheAdherent {...sharedProps} selectedId={selectedAdherentId} setSelectedId={setSelectedAdherentId} goToEtab={goToEtab} />
        )}
        {page === 'etablissements' && (
          <FicheEtablissement {...sharedProps} selectedId={selectedEtabId} setSelectedId={setSelectedEtabId} goToAdherent={goToAdherent} />
        )}
        {page === 'recherche' && <RechercheAvancee {...sharedProps} />}
        {page === 'statistiques' && <Statistiques {...sharedProps} />}
        {page === 'adhesion' && <NouvelleAdhesion {...sharedProps} onSubmitted={() => setPage(currentRole === 'tresorier' ? 'validation' : currentRole === 'delegue' ? 'accueil_ds' : 'adherents')} />}
        {page === 'validation' && <ValidationAdhesions {...sharedProps} />}
        {page === 'conseil' && <ConseilSyndical {...sharedProps} />}
        {page === 'comptabilite' && <ComptabiliteFinancier {...sharedProps} />}
        {page === 'depenses' && (
          <SuiviDepenses
            depenses={data.depenses || []}
            budgetDepenses={data.budgetDepenses || {}}
            selectedYear={data.selectedYear}
            saveDepense={saveDepense}
            deleteDepense={deleteDepense}
            saveBudget={saveBudget}
            currentRole={currentRole}
          />
        )}
      </main>

      {/* Settings modal */}
      {showSettings && (
        <SettingsModal
          users={data.users || []}
          saveUser={saveUser}
          deleteUser={deleteUser}
          isAdmin={isAdmin}
          currentRole={currentRole}
          onClose={() => setShowSettings(false)}
          loadDemo={loadDemo}
          loadSeedData={loadSeedData}
          loadVirement={loadVirement}
          adherentsCount={data.adherents.length}
          etablissements={data.etablissements || []}
          connexions={data.connexions || []}
          exportBackup={exportBackup}
          importBackup={importBackup}
          backupKey={BACKUP_KEY}
          restoreAutoBackup={(saved) => { setData(saved.data); immediateSync.current = true; }}
        />
      )}
    </div>
  );
}

const ROLE_OPTIONS = [
  { value: 'tresorier',     label: 'Accès élargi (bureau)' },
  { value: 'bureau_simple', label: 'Membre du bureau (lecture seule)' },
  { value: 'delegue',       label: 'Délégué(e) syndical(e)' },
];
const TITLE_OPTIONS = [
  'Secrétaire général(e)', 'Secrétaire adjoint(e)', 'Trésorier(ère)',
  'Trésorier(ère) adjoint(e)', 'Commission de contrôle', 'Délégué(e) syndical(e)', 'Administrateur',
];

function SettingsModal({ users, saveUser, deleteUser, isAdmin, currentRole, onClose, loadDemo, loadSeedData, loadVirement, adherentsCount, etablissements, connexions, exportBackup, importBackup, backupKey, restoreAutoBackup }) {
  const [tab, setTab] = useState(isAdmin ? 'users' : 'data');
  const backupFileRef = React.useRef();
  const autoBackup = React.useMemo(() => {
    try { return JSON.parse(localStorage.getItem(backupKey) || 'null'); } catch { return null; }
  }, [backupKey]);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState({ username: '', password: '', confirmPwd: '', role: 'tresorier', roleTitle: 'Secrétaire général(e)', isAdmin: false, email: '', etablissementsIds: [] });
  const [err, setErr] = useState('');
  const [ok, setOk]   = useState('');

  const openCreate = () => {
    setEditUser({});
    setForm({ username: '', password: '', confirmPwd: '', role: 'tresorier', roleTitle: 'Secrétaire général(e)', isAdmin: false, email: '', etablissementsIds: [] });
    setErr(''); setOk('');
  };
  const openEdit = (u) => {
    setEditUser(u);
    setForm({ username: u.username, password: '', confirmPwd: '', role: u.role, roleTitle: u.roleTitle, isAdmin: u.isAdmin || false, email: u.email || '', etablissementsIds: u.etablissementsIds || [] });
    setErr(''); setOk('');
  };
  const handleSave = async () => {
    if (!form.username.trim()) { setErr('Identifiant requis.'); return; }
    const isNew = !editUser?.id;
    if (isNew && !form.password) { setErr('Mot de passe initial requis.'); return; }
    if (form.password && form.password.length < 4) { setErr('Mot de passe trop court (min. 4 car.).'); return; }
    if (form.password && form.password !== form.confirmPwd) { setErr('Les mots de passe ne correspondent pas.'); return; }
    if (isNew && users.find(u => u.username === form.username.trim())) { setErr('Cet identifiant existe déjà.'); return; }
    const pwdToStore = form.password ? await hashPassword(form.password) : editUser?.password;
    const saved = {
      ...(editUser?.id ? editUser : {}),
      username: form.username.trim(),
      password: pwdToStore,
      role: form.role,
      roleTitle: form.roleTitle,
      isAdmin: form.isAdmin,
      email: form.email.trim().toLowerCase(),
      mustChangePassword: isNew ? true : (editUser?.mustChangePassword ?? false),
      etablissementsIds: form.role === 'delegue' ? form.etablissementsIds : [],
    };
    saveUser(saved);
    setEditUser(null); setOk(isNew ? `Utilisateur "${saved.username}" créé.` : 'Modifications enregistrées.');
    setTimeout(() => setOk(''), 3000);
  };
  const handleDelete = (u) => {
    if (window.confirm(`Supprimer l'utilisateur "${u.username}" ?`)) {
      deleteUser(u.id);
      setOk(`Utilisateur "${u.username}" supprimé.`); setTimeout(() => setOk(''), 2500);
    }
  };
  const handleResetPwd = async (u) => {
    const np = window.prompt(`Nouveau mot de passe temporaire pour "${u.username}" :`);
    if (!np || np.length < 4) { alert('Mot de passe trop court ou annulé.'); return; }
    const hashed = await hashPassword(np);
    saveUser({ ...u, password: hashed, mustChangePassword: true });
    setOk(`Mot de passe de "${u.username}" réinitialisé. Il devra le changer à la prochaine connexion.`);
    setTimeout(() => setOk(''), 4000);
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <span className="modal-title">⚙️ Administration</span>
          <button className="btn-icon" onClick={onClose} title="Fermer">✕</button>
        </div>
        <div className="tabs" style={{ marginBottom: 16 }}>
          {isAdmin && <button className={`tab-btn${tab==='users'?' active':''}`} onClick={() => { setTab('users'); setEditUser(null); }}>👤 Utilisateurs</button>}
          {isAdmin && <button className={`tab-btn${tab==='connexions'?' active':''}`} onClick={() => { setTab('connexions'); setEditUser(null); }}>🔗 Connexions</button>}
          {isAdmin && <button className={`tab-btn${tab==='data'?' active':''}`} onClick={() => { setTab('data'); setEditUser(null); }}>📂 Données</button>}
          {!isAdmin && <button className={`tab-btn${tab==='data'?' active':''}`} onClick={() => setTab('data')}>📂 Données</button>}
          <button className={`tab-btn${tab==='backup'?' active':''}`} onClick={() => { setTab('backup'); setEditUser(null); }}>💾 Sauvegarde</button>
        </div>

        {ok && <div className="alert alert-success mb-3" style={{ fontSize: 13 }}>{ok}</div>}
        {err && <div className="alert alert-danger mb-3" style={{ fontSize: 13 }}>{err}</div>}

        {tab === 'users' && (
          <div>
            {/* User list */}
            {!editUser && (
              <>
                <div className="alert alert-info mb-3" style={{ fontSize: 12 }}>
                  <span>ℹ️</span>
                  <div><strong>Accès élargi</strong> : toutes les fonctions de gestion.<br/>
                  <strong>Membre bureau (lecture)</strong> : dépenses et adhérents en consultation uniquement.<br/>
                  <strong>Délégué(e) syndical(e)</strong> : recherche adhérents (établissements assignés), demandes RIB/mode paiement, nouvelles adhésions via son espace.</div>
                </div>
                <div style={{ marginBottom: 14 }}>
                  {users.map(u => (
                    <div key={u.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 12px', borderRadius:8, border:'1px solid #eee', marginBottom:6, background: u.isAdmin ? '#fff9f0' : '#fff' }}>
                      <div style={{ fontSize:22 }}>{u.isAdmin ? '🔑' : '👤'}</div>
                      <div style={{ flex:1 }}>
                        <div style={{ fontWeight:700, fontSize:14 }}>{u.username}</div>
                        <div style={{ fontSize:11, color:'#888', marginTop:1 }}>
                          {u.role === 'tresorier' ? 'Accès élargi' : u.role === 'bureau_simple' ? 'Bureau lecture' : 'Délégué syndical'}
                          {u.role === 'delegue' && (u.etablissementsIds || []).length > 0 && (
                            <span style={{ marginLeft:6, background:'#e0f2fe', color:'#0369a1', borderRadius:4, padding:'1px 5px', fontSize:10 }}>
                              {(u.etablissementsIds).length} étab.
                            </span>
                          )}
                          {u.role === 'delegue' && (u.etablissementsIds || []).length === 0 && (
                            <span style={{ marginLeft:6, background:'#fef3c7', color:'#92400e', borderRadius:4, padding:'1px 5px', fontSize:10 }}>tous étab.</span>
                          )}
                          {u.isAdmin && <span style={{ marginLeft:6, background:'#fff3cd', color:'#856404', borderRadius:4, padding:'1px 5px', fontSize:10, fontWeight:700 }}>ADMIN</span>}
                          {u.mustChangePassword && <span style={{ marginLeft:6, background:'#fff3e0', color:'#e65100', borderRadius:4, padding:'1px 5px', fontSize:10 }}>⚠ doit changer mdp</span>}
                        </div>
                      </div>
                      <div style={{ display:'flex', gap:4 }}>
                        <button className="btn btn-outline btn-xs" title="Réinitialiser le mot de passe" onClick={() => handleResetPwd(u)}>🔄 Mdp</button>
                        <button className="btn btn-outline btn-xs" title="Modifier" onClick={() => openEdit(u)}>✏️</button>
                        <button className="btn btn-outline btn-xs" title="Supprimer" onClick={() => handleDelete(u)} style={{ color:'#cc0000' }}>🗑️</button>
                      </div>
                    </div>
                  ))}
                </div>
                <button className="btn btn-primary btn-sm" onClick={openCreate}>+ Créer un utilisateur</button>
              </>
            )}

            {/* Create / Edit form */}
            {editUser !== null && (
              <div>
                <div style={{ fontWeight:700, fontSize:14, marginBottom:14 }}>
                  {editUser?.id ? `✏️ Modifier "${editUser.username}"` : '➕ Nouvel utilisateur'}
                </div>
                <div className="form-group">
                  <label className="form-label">Identifiant de connexion</label>
                  <input className="form-control" value={form.username} onChange={e => { setForm(f=>({...f,username:e.target.value})); setErr(''); }}
                    placeholder="ex: stephane.lebon" disabled={!!editUser?.id} />
                </div>
                <div className="form-group">
                  <label className="form-label">Adresse e-mail (pour réinitialisation mot de passe)</label>
                  <input className="form-control" type="email" value={form.email} onChange={e => { setForm(f=>({...f,email:e.target.value})); setErr(''); }}
                    placeholder="ex: prenom.nom@exemple.fr" />
                </div>
                <div className="form-group">
                  <label className="form-label">Fonction / Titre affiché</label>
                  <select className="form-control" value={form.roleTitle} onChange={e => setForm(f=>({...f,roleTitle:e.target.value}))}>
                    {TITLE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Niveau d'accès</label>
                  <select className="form-control" value={form.role} onChange={e => setForm(f=>({...f,role:e.target.value}))}>
                    {ROLE_OPTIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </div>
                <div className="grid-2">
                  <div className="form-group">
                    <label className="form-label">{editUser?.id ? 'Nouveau mot de passe (laisser vide = inchangé)' : 'Mot de passe initial *'}</label>
                    <input className="form-control" type="password" value={form.password} onChange={e => { setForm(f=>({...f,password:e.target.value})); setErr(''); }} placeholder="••••••" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Confirmer</label>
                    <input className="form-control" type="password" value={form.confirmPwd} onChange={e => { setForm(f=>({...f,confirmPwd:e.target.value})); setErr(''); }} placeholder="••••••" />
                  </div>
                </div>
                {form.role === 'delegue' && (
                  <div className="form-group">
                    <label className="form-label">Établissements visibles <span style={{ color:'#9ca3af', fontWeight:400 }}>(laisser vide = tous)</span></label>
                    <div style={{ border:'1px solid #e5e7eb', borderRadius:8, padding:'10px 12px', maxHeight:160, overflowY:'auto', background:'#fafafa' }}>
                      {(etablissements || []).length === 0 ? (
                        <div style={{ fontSize:12, color:'#9ca3af' }}>Aucun établissement créé.</div>
                      ) : (
                        (etablissements || []).map(etab => (
                          <label key={etab.id} style={{ display:'flex', alignItems:'center', gap:8, padding:'4px 0', cursor:'pointer', fontSize:13 }}>
                            <input
                              type="checkbox"
                              checked={(form.etablissementsIds || []).includes(etab.id)}
                              onChange={e => {
                                const ids = form.etablissementsIds || [];
                                setForm(f => ({ ...f, etablissementsIds: e.target.checked ? [...ids, etab.id] : ids.filter(id => id !== etab.id) }));
                              }}
                              style={{ accentColor:'#CC0000' }}
                            />
                            {etab.nom}
                          </label>
                        ))
                      )}
                    </div>
                    {(form.etablissementsIds || []).length === 0 && (
                      <div style={{ fontSize:11, color:'#d97706', marginTop:4 }}>Sans sélection, le délégué verra tous les adhérents.</div>
                    )}
                  </div>
                )}
                <div className="form-group" style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <input type="checkbox" id="chk-admin" checked={form.isAdmin} onChange={e => setForm(f=>({...f,isAdmin:e.target.checked}))} />
                  <label htmlFor="chk-admin" style={{ fontSize:13, cursor:'pointer' }}>Droits administrateur (peut gérer les utilisateurs)</label>
                </div>
                <div style={{ display:'flex', gap:8, marginTop:4 }}>
                  <button className="btn btn-primary" onClick={handleSave}>� Enregistrer</button>
                  <button className="btn btn-outline" onClick={() => { setEditUser(null); setErr(''); }}>Annuler</button>
                </div>
              </div>
            )}
          </div>
        )}

        {tab === 'connexions' && (
          <div>
            <div style={{ fontSize:12, color:'#6b7280', marginBottom:12 }}>
              Historique des <strong>{(connexions||[]).length}</strong> dernières connexions (100 max) · Sessions par appareil et navigateur
            </div>
            {(connexions||[]).length === 0 ? (
              <div style={{ textAlign:'center', color:'#9ca3af', padding:'24px 0', fontSize:13 }}>Aucune connexion enregistrée</div>
            ) : (
              <div style={{ maxHeight:380, overflowY:'auto' }}>
                {(connexions||[]).map(c => (
                  <div key={c.id} style={{ display:'flex', alignItems:'flex-start', gap:10, padding:'8px 10px', borderRadius:6, border:'1px solid #f0f0f0', marginBottom:5, background:'#fafafa' }}>
                    <span style={{ fontSize:20 }}>{c.device?.startsWith('📱') ? '📱' : '🖥️'}</span>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
                        <strong style={{ fontSize:13 }}>{c.username}</strong>
                        <span style={{ background:'#e0f2fe', color:'#0369a1', borderRadius:4, padding:'1px 6px', fontSize:10, fontWeight:700 }}>{c.roleTitle || c.role}</span>
                        <span style={{ fontSize:10, color:'#9ca3af' }}>{c.browser}</span>
                        <span style={{ fontSize:10, color:'#9ca3af' }}>{c.device}</span>
                      </div>
                      <div style={{ fontSize:11, color:'#6b7280', marginTop:2 }}>
                        {new Date(c.ts).toLocaleDateString('fr-FR', { weekday:'short', day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' })}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === 'data' && (
          <div>
            <div style={{ background:'#f0f7ff', border:'1px solid #b3d4f5', borderRadius:8, padding:'14px 16px', marginBottom:16 }}>
              <div style={{ fontWeight:700, fontSize:14, marginBottom:6 }}>📥 Importer la base réelle</div>
              <p style={{ fontSize:13, color:'#444', marginBottom:10 }}>Charge les <strong>{SEED_ADHERENTS.length} adhérents</strong> du fichier <em>Base_Adherents_SDASSP_FO_Avril2026_V2</em> (prélèvements SEPA, IBAN, adresses, e-mails).</p>
              <button className="btn btn-primary" onClick={() => { if (adherentsCount > 0 && !window.confirm(`Remplacer les ${adherentsCount} adhérent(s) existants par la base réelle ?`)) return; loadSeedData(); onClose(); }}>
                📥 Charger la base ({SEED_ADHERENTS.length} adhérents)
              </button>
            </div>
            <div style={{ background:'#f0fff4', border:'1px solid #a3d9a5', borderRadius:8, padding:'14px 16px', marginBottom:16 }}>
              <div style={{ fontWeight:700, fontSize:14, marginBottom:6 }}>📊 Importer Virement 2023</div>
              <p style={{ fontSize:13, color:'#444', marginBottom:10 }}>Importe <strong>{SEED_VIREMENT.length} adhérents</strong> payant par virement depuis le fichier Excel 2023. Les noms/prénoms déjà présents sont ignorés automatiquement.</p>
              <button className="btn btn-success btn-sm" onClick={() => { loadVirement(); onClose(); }}>
                📥 Importer Virement 2023 ({SEED_VIREMENT.length} personnes)
              </button>
            </div>
            <div style={{ borderTop:'1px solid #eee', paddingTop:14 }}>
              <p style={{ fontSize:13, color:'#888', marginBottom:10 }}>Ou charger 12 adhérents fictifs pour tester l'application.</p>
              <button className="btn btn-outline btn-sm" onClick={() => { loadDemo(); onClose(); }} disabled={adherentsCount > 0}>
                📂 Données démo
              </button>
              {adherentsCount > 0 && <div className="alert alert-warning mt-3" style={{fontSize:12}}>⚠ Des données existent déjà ({adherentsCount} adhérents).</div>}
            </div>
          </div>
        )}

        {tab === 'backup' && (
          <div>
            <div style={{ background:'#f0fdf4', border:'1px solid #86efac', borderRadius:10, padding:'18px 20px', marginBottom:16 }}>
              <div style={{ fontWeight:700, fontSize:15, marginBottom:6 }}>⬇️ Exporter une sauvegarde</div>
              <p style={{ fontSize:13, color:'#444', marginBottom:14 }}>Télécharge un fichier <code>.json</code> contenant <strong>toutes vos données</strong> (adhérents, cotisations, comptabilité, utilisateurs…). Enregistrez-le sur un disque dur externe, une clé USB ou Google Drive.</p>
              <button className="btn btn-primary" onClick={exportBackup}>
                ⬇️ Télécharger la sauvegarde du {new Date().toLocaleDateString('fr-FR')}
              </button>
            </div>
            <div style={{ background:'#fff7ed', border:'1px solid #fdba74', borderRadius:10, padding:'18px 20px', marginBottom:16 }}>
              <div style={{ fontWeight:700, fontSize:15, marginBottom:6 }}>⬆️ Restaurer une sauvegarde</div>
              <p style={{ fontSize:13, color:'#444', marginBottom:6 }}>Sélectionnez un fichier <code>.json</code> précédemment exporté pour restaurer toutes vos données.</p>
              <div style={{ fontSize:12, color:'#b45309', background:'#fef3c7', borderRadius:6, padding:'8px 12px', marginBottom:12 }}>⚠️ Attention : cette action remplace toutes les données actuelles par celles du fichier.</div>
              <input ref={backupFileRef} type="file" accept=".json" style={{ display:'none' }}
                onChange={e => { const f = e.target.files[0]; if (f) { importBackup(f); e.target.value = ''; } }} />
              <button className="btn btn-outline btn-sm" style={{ borderColor:'#f97316', color:'#c2410c' }}
                onClick={() => backupFileRef.current?.click()}>
                📂 Choisir un fichier de sauvegarde…
              </button>
            </div>
            {autoBackup && (
              <div style={{ background:'#eff6ff', border:'1px solid #bfdbfe', borderRadius:10, padding:'18px 20px', marginBottom:16 }}>
                <div style={{ fontWeight:700, fontSize:15, marginBottom:6 }}>🔄 Sauvegarde automatique locale</div>
                <p style={{ fontSize:13, color:'#444', marginBottom:6 }}>
                  Dernière sauvegarde automatique : <strong>{new Date(autoBackup.savedAt).toLocaleDateString('fr-FR', { weekday:'long', day:'numeric', month:'long', year:'numeric' })}</strong>
                  <br/>Contient <strong>{autoBackup.adherentsCount} adhérent(s)</strong>.
                </p>
                <div style={{ fontSize:12, color:'#1e40af', background:'#dbeafe', borderRadius:6, padding:'8px 12px', marginBottom:12 }}>ℹ️ Cette sauvegarde est stockée dans ce navigateur. Elle est indépendante de la sauvegarde principale.</div>
                <button className="btn btn-outline btn-sm" style={{ borderColor:'#3b82f6', color:'#1d4ed8' }}
                  onClick={() => {
                    if (window.confirm(`Restaurer la sauvegarde du ${new Date(autoBackup.savedAt).toLocaleDateString('fr-FR')} (${autoBackup.adherentsCount} adhérents) ?`)) {
                      restoreAutoBackup(autoBackup); onClose();
                    }
                  }}>
                  🔄 Restaurer cette sauvegarde locale
                </button>
              </div>
            )}
            <div style={{ fontSize:12, color:'#9ca3af', background:'#f9fafb', borderRadius:8, padding:'10px 14px' }}>
              💡 <strong>Conseil :</strong> effectuez une sauvegarde avant chaque assemblée générale et après chaque import de relevé bancaire.
            </div>
          </div>
        )}

        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>Fermer</button>
        </div>
      </div>
    </div>
  );
}
